import { useState, type FormEvent } from "react";
import { confirmSignIn } from "aws-amplify/auth";
import { ErrorMessage } from "./error-message.js";
import { friendlyAuthError } from "./cognito-errors.js";

/**
 * Renders when Amplify returns `nextStep.signInStep ===
 * 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'`. The user types the 6-digit code
 * from their authenticator app; we call `confirmSignIn`.
 *
 * Input attributes: `inputMode="numeric"` shows the numeric keyboard
 * on mobile; `autoComplete="one-time-code"` taps into iOS/Android
 * SMS-code-style autofill (the password manager paste path also works
 * for TOTP codes).
 */
export function MfaChallengeForm({
  onSignedIn,
}: {
  onSignedIn: () => void;
}) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await confirmSignIn({ challengeResponse: code });
      if (result.isSignedIn) {
        onSignedIn();
      } else {
        // Unexpected: a TOTP code response should either succeed or
        // throw. Surface the next-step name so the user has *something*.
        setError(`Unexpected next step: ${result.nextStep.signInStep}`);
      }
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
      <div>
        <h2 className="text-lg font-medium">Two-factor code</h2>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
          Open your authenticator app and enter the 6-digit code for Hearth.
        </p>
      </div>
      <div>
        <label htmlFor="mfa-code" className="sr-only">
          Authentication code
        </label>
        <input
          id="mfa-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          autoFocus
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-center font-mono text-2xl tracking-widest focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
          disabled={submitting}
          placeholder="000000"
        />
      </div>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <button
        type="submit"
        disabled={submitting || code.length !== 6}
        className="w-full rounded-md bg-brand-500 px-4 py-2.5 text-base font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? "Verifying…" : "Verify"}
      </button>
    </form>
  );
}
